import INFRAMARK_CONSTANTS from "common/Constants/inframark-constants";
import {
  ALL_EFFECTIVE_ACCESS_PERMISSIONS,
  APPLICATION_RETURN_PERMISSIONS,
  EFFECTIVE_ACCESS_PAGE_DEFINITIONS,
  EFFECTIVE_ACCESS_PAGE_KEY,
} from "common/Constants/EffectiveAccess.constants";
import { isNonePlatformRole, isPlatformAdminProfile } from "common/Utilities/Role.utils";

export const NO_EFFECTIVE_ROUTE_ACCESS_MESSAGE =
  "No access available at this moment. Please contact your administrator or try again later.";

export const normalizePermissionSet = (permissions) =>
  new Set(
    (permissions ?? [])
      .map((permission) => String(permission ?? "").trim())
      .filter(Boolean)
  );

export const hasFullEffectivePermissionAccess = (permissions) => {
  const granted = normalizePermissionSet(permissions);
  return ALL_EFFECTIVE_ACCESS_PERMISSIONS.every((permission) =>
    granted.has(permission)
  );
};

export const hasAnyApplicationReturnPermission = (effectiveAccess) => {
  if (hasFullEffectivePermissionAccess(effectiveAccess?.permissions)) {
    return true;
  }
  const granted = normalizePermissionSet(effectiveAccess?.permissions);
  return APPLICATION_RETURN_PERMISSIONS.some((permission) =>
    granted.has(permission)
  );
};

const pagePermissionKeys = (page) => {
  if (Array.isArray(page?.actions) && page.actions.length) {
    return page.actions.flatMap((action) => action.permissions ?? []);
  }
  return page?.permissions ?? [];
};

const resolveActionAccess = (action, granted, hasFullAccess) => {
  const required = action?.permissions ?? [];
  const grantedForAction = required.filter((permission) => granted.has(permission));
  return {
    actionKey: action.actionKey,
    label: action.label,
    hasAccess: hasFullAccess || grantedForAction.length > 0,
    grantedPermissions: grantedForAction,
  };
};

export const resolveEffectivePageAccessList = (effectiveAccess) => {
  const granted = normalizePermissionSet(effectiveAccess?.permissions);
  const hasFullAccess = hasFullEffectivePermissionAccess(
    effectiveAccess?.permissions
  );

  return EFFECTIVE_ACCESS_PAGE_DEFINITIONS.map((page) => {
    const requiredPermissions = pagePermissionKeys(page);
    const grantedPermissions = requiredPermissions.filter((permission) =>
      granted.has(permission)
    );
    const hasAccess = hasFullAccess || grantedPermissions.length > 0;

    const actions = Array.isArray(page.actions)
      ? page.actions.map((action) =>
          resolveActionAccess(action, granted, hasFullAccess)
        )
      : undefined;

    return {
      pageKey: page.pageKey,
      pageLabel: page.pageLabel,
      actionsSummary: page.actionsSummary,
      routeValue: page.routeValue,
      hasAccess,
      grantedPermissions: hasFullAccess ? [...requiredPermissions] : grantedPermissions,
      actions,
    };
  });
};

export const getAccessibleRouteValuesFromEffectiveAccess = (effectiveAccess) => {
  if (hasFullEffectivePermissionAccess(effectiveAccess?.permissions)) {
    return ["all"];
  }

  const pages = resolveEffectivePageAccessList(effectiveAccess);
  return pages
    .filter((page) => page.hasAccess && page.routeValue)
    .map((page) => page.routeValue);
};

export const hasEffectivePageAccess = (effectiveAccess, pageKey) =>
  resolveEffectivePageAccessList(effectiveAccess).find(
    (page) => page.pageKey === pageKey
  )?.hasAccess ?? false;

export const hasDataManagementAccess = (
  profile,
  { usesEffectiveAccess = false, canAccessPage = () => false } = {}
) => {
  if (usesEffectiveAccess) {
    return canAccessPage(EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT);
  }

  const accessTo = (profile?.accessTo ?? []).map((key) =>
    String(key).toLowerCase()
  );
  return accessTo.includes("all") || accessTo.includes("datamanagement");
};

export const normalizeRoutePathname = (pathname = "") => {
  const normalized = String(pathname).split("?")[0].replace(/\/+$/, "");
  return normalized || "/";
};

export const getAdministrationRoutePath = () =>
  INFRAMARK_CONSTANTS?.navigationRoutes?.administration ?? "/administration";

export const isExternalAppUrl = (url = "") =>
  /^https?:\/\//i.test(String(url).trim());

export const getDashboardRedirectUrl = () => {
  const { base_url } = require("common/HttpClient/http-client");
  const normalizedBase = String(base_url ?? "").trim().replace(/\/$/, "");
  return normalizedBase ? `${normalizedBase}/live` : null;
};

export const redirectToLiveApplication = () => {
  const dashboardUrl = getDashboardRedirectUrl();
  if (dashboardUrl) {
    window.location.href = dashboardUrl;
  }
};

export const usesMeEffectiveAccess = (profile) =>
  Boolean(profile?.sessionFromMe && profile?.effectiveAccess);

export const shouldNoneUserUseAdministrationLanding = (profile) => {
  if (!isNonePlatformRole(profile) || !usesMeEffectiveAccess(profile)) {
    return false;
  }
  return (
    hasEffectivePageAccess(
      profile.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT
    ) ||
    hasEffectivePageAccess(
      profile.effectiveAccess,
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT
    )
  );
};

export const hasDashboardPageAccess = (profile) => {
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }
  return (
    hasEffectivePageAccess(profile.effectiveAccess, EFFECTIVE_ACCESS_PAGE_KEY.HOME) ||
    hasAnyApplicationReturnPermission(profile.effectiveAccess)
  );
};

const hasAnyEffectiveAdminPageAccess = (profile) => {
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }
  const adminPageKeys = [
    EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT,
    EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT,
    EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
    EFFECTIVE_ACCESS_PAGE_KEY.CONFIGURATIONS,
    EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
  ];
  return adminPageKeys.some((pageKey) =>
    hasEffectivePageAccess(profile.effectiveAccess, pageKey)
  );
};

const hasEffectivePageKeysAccess = (profile, pageKeys = []) => {
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }
  return pageKeys.some((pageKey) =>
    hasEffectivePageAccess(profile.effectiveAccess, pageKey)
  );
};

const isPlatformAdminScopedPlatformRoute = (path) => {
  if (path === "/" || path === "/home") {
    return true;
  }
  if (path === "/customers" || path.startsWith("/customers/")) {
    return true;
  }
  if (path === "/createcustomer" || path.startsWith("/createcustomer/")) {
    return true;
  }
  if (path === "/thingtype" || path.startsWith("/thingtype/")) {
    return true;
  }
  if (path === "/user-access" || path.startsWith("/user-access/")) {
    return true;
  }
  return false;
};

export const canAccessRouteForProfile = (profile, pathname) => {
  if (!usesMeEffectiveAccess(profile)) {
    return true;
  }

  const path = normalizeRoutePathname(pathname);

  if (path === "/confirm-reset-password" || path === "/access_denied") {
    return true;
  }

  if (isPlatformAdminProfile(profile) && isPlatformAdminScopedPlatformRoute(path)) {
    return true;
  }

  if (path === "/" || path === "/home") {
    return (
      hasAnyEffectiveAdminPageAccess(profile) ||
      hasDashboardPageAccess(profile)
    );
  }

  if (path === "/admin" || path.startsWith("/admin/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT,
    ]);
  }

  if (path === "/administration" || path.startsWith("/administration/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.DATA_MANAGEMENT,
      EFFECTIVE_ACCESS_PAGE_KEY.USER_MANAGEMENT,
    ]);
  }

  if (path === "/customers" || path.startsWith("/customers/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
    ]);
  }

  if (path === "/createcustomer" || path.startsWith("/createcustomer/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.CUSTOMER,
    ]);
  }

  if (path === "/thingtype" || path.startsWith("/thingtype/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.CONFIGURATIONS,
    ]);
  }

  if (path === "/user-access" || path.startsWith("/user-access/")) {
    return hasEffectivePageKeysAccess(profile, [
      EFFECTIVE_ACCESS_PAGE_KEY.USER_ACCESS_CONTROL,
    ]);
  }

  if (path === "/dashboard" || path.startsWith("/dashboard/")) {
    return hasDashboardPageAccess(profile);
  }

  const cloudHierarchy =
    INFRAMARK_CONSTANTS?.navigationRoutes?.cloudHierarchy ?? "/cloud-hierarchy";
  const normalizedCloudPath = normalizeRoutePathname(cloudHierarchy);
  if (path === normalizedCloudPath || path.startsWith(`${normalizedCloudPath}/`)) {
    return hasDashboardPageAccess(profile);
  }

  return true;
};

export const resolveNavigationAfterEffectiveAccessChange = (profile, pathname) => {
  if (!usesMeEffectiveAccess(profile)) {
    return { action: "stay" };
  }

  if (canAccessRouteForProfile(profile, pathname)) {
    return { action: "stay" };
  }

  if (hasDashboardPageAccess(profile)) {
    const dashboardUrl = getDashboardRedirectUrl();
    if (dashboardUrl) {
      return {
        action: "redirect",
        path: dashboardUrl,
        useFullPageLoad: true,
      };
    }
  }

  return {
    action: "no_access",
    message: NO_EFFECTIVE_ROUTE_ACCESS_MESSAGE,
  };
};

export const applyNavigationAfterEffectiveAccessChange = (
  profile,
  pathname,
  navigate
) => {
  const resolution = resolveNavigationAfterEffectiveAccessChange(profile, pathname);
  if (resolution.action === "redirect") {
    if (resolution.useFullPageLoad) {
      window.location.assign(resolution.path);
    } else if (typeof navigate === "function") {
      navigate(resolution.path, { replace: true });
    }
  }
  return resolution;
};

export const resolveNavigationAfterCustomerSwitch = (profile) => {
  const administrationPath = getAdministrationRoutePath();

  if (isNonePlatformRole(profile)) {
    return {
      action: "redirect",
      path: administrationPath,
      useFullPageLoad: true,
    };
  }

  const resolution = resolveNavigationAfterEffectiveAccessChange(
    profile,
    administrationPath
  );

  if (resolution.action === "stay") {
    return {
      action: "redirect",
      path: administrationPath,
      useFullPageLoad: false,
    };
  }

  return resolution;
};

export const applyNavigationAfterCustomerSwitch = (profile, navigate) => {
  const resolution = resolveNavigationAfterCustomerSwitch(profile);

  if (resolution.action === "redirect") {
    if (resolution.useFullPageLoad) {
      window.location.assign(resolution.path);
      return resolution;
    }
    if (typeof navigate === "function") {
      navigate(resolution.path, { replace: true });
    }
    return resolution;
  }

  if (resolution.action === "no_access" && typeof navigate === "function") {
    navigate(getAdministrationRoutePath(), { replace: true });
  }

  return resolution;
};
